import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authRepository } from '../../infrastructure/authApiRepository';
import { PasswordToggleButton } from '../components/PasswordToggleButton';
import '../styles/auth.css';

const MIN_PW_LEN = 8;
const MAX_PW_LEN = 20;

const PW_CHECKS = [
    { label: '8~20자',         test: (v) => v.length >= MIN_PW_LEN && v.length <= MAX_PW_LEN },
    { label: '영어 대문자 포함', test: (v) => /[A-Z]/.test(v) },
    { label: '영어 소문자 포함', test: (v) => /[a-z]/.test(v) },
    { label: '숫자 포함',       test: (v) => /[0-9]/.test(v) },
];

export function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [form, setForm] = useState({ password: '', passwordConfirm: '' });
    const [fieldError, setFieldError] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const [passwordTouched, setPasswordTouched] = useState(false);
    const [passwordBlurred, setPasswordBlurred] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    const set = (field) => (e) => {
        const value = e.target.value;
        setForm((f) => {
            const next = { ...f, [field]: value };
            if (field === 'passwordConfirm' || field === 'password') {
                const pw  = field === 'password'        ? value : f.password;
                const cpw = field === 'passwordConfirm' ? value : f.passwordConfirm;
                if (cpw) {
                    setFieldError((fe) => ({ ...fe, passwordConfirm: pw !== cpw ? '비밀번호가 일치하지 않습니다.' : '' }));
                }
            } else if (fieldError[field]) {
                setFieldError((fe) => ({ ...fe, [field]: '' }));
            }
            return next;
        });
        if (error) setError('');
    };

    function validate() {
        const errs = {};
        if (!form.password) errs.password = '비밀번호를 입력해주세요.';
        else if (!PW_CHECKS.every(({ test }) => test(form.password))) errs.password = 'invalid';
        if (!form.passwordConfirm) errs.passwordConfirm = '비밀번호 확인을 입력해주세요.';
        else if (form.password !== form.passwordConfirm) errs.passwordConfirm = '비밀번호가 일치하지 않습니다.';
        return errs;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const errs = validate();
        setFieldError(errs);
        if (errs.password) { setPasswordTouched(true); setPasswordBlurred(true); }
        if (Object.keys(errs).length > 0) return;
        setError('');
        setLoading(true);
        try {
            await authRepository.confirmPasswordReset({ token, newPassword: form.password });
            setDone(true);
        } catch (err) {
            if (err.status === 400) setError('유효하지 않거나 만료된 링크입니다. 비밀번호 찾기를 다시 시도해주세요.');
            else setError(err.message || '요청에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }

    if (!token) {
        return (
            <div className="auth-page">
                <div className="auth-card">
                    <Link to="/" className="auth-card__logo">
                        <img src="/images/logo/main_logo.png" alt="RISING BROKERS" />
                    </Link>
                    <div className="auth-success">
                        <div className="auth-success__title">잘못된 접근입니다</div>
                        <div className="auth-success__desc">이메일로 발송된 링크를 통해 접근해주세요.</div>
                        <Link to="/forgot-password" className="auth-submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                            비밀번호 찾기
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (done) {
        return (
            <div className="auth-page">
                <div className="auth-card">
                    <Link to="/" className="auth-card__logo">
                        <img src="/images/logo/main_logo.png" alt="RISING BROKERS" />
                    </Link>
                    <div className="auth-success">
                        <div className="auth-success__title">비밀번호가 변경되었습니다</div>
                        <div className="auth-success__desc">새 비밀번호로 로그인해주세요.</div>
                        <Link to="/login" className="auth-submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                            로그인하기
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-card">
                <Link to="/" className="auth-card__logo">
                    <img src="/images/logo/main_logo.png" alt="RISING BROKERS" />
                </Link>
                <div className="auth-card__title">새 비밀번호 설정</div>
                <div className="auth-card__sub">사용할 새 비밀번호를 입력해주세요</div>

                <form className="auth-form" onSubmit={handleSubmit} noValidate>
                    <div className="auth-field">
                        <label>새 비밀번호</label>
                        <div className="auth-input-wrap">
                            <input
                                className={`auth-input${fieldError.password === 'invalid' || (fieldError.password && !form.password) ? ' auth-input--error' : ''}`}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="8~20자, 대소문자 + 숫자 포함"
                                value={form.password}
                                onChange={set('password')}
                                onFocus={() => setPasswordTouched(true)}
                                onBlur={() => setPasswordBlurred(true)}
                                autoComplete="new-password"
                                maxLength={MAX_PW_LEN}
                                autoFocus
                            />
                            <PasswordToggleButton visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                        </div>
                        {fieldError.password && fieldError.password !== 'invalid' && (
                            <span className="auth-field__error">{fieldError.password}</span>
                        )}
                        {passwordTouched && (
                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {PW_CHECKS.map(({ label, test }) => {
                                    const met = test(form.password);
                                    const showFail = !met && (passwordBlurred || fieldError.password === 'invalid');
                                    const color = met ? '#22c55e' : showFail ? '#e53935' : '#9ca3af';
                                    return (
                                        <span key={label} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, color }}>
                                            <span style={{ fontSize: 13, lineHeight: 1 }}>{met ? '✓' : '○'}</span>
                                            {label}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="auth-field">
                        <label>새 비밀번호 확인</label>
                        <div className="auth-input-wrap">
                            <input
                                className={`auth-input${(form.passwordConfirm && form.password !== form.passwordConfirm) || fieldError.passwordConfirm ? ' auth-input--error' : (form.passwordConfirm && form.password === form.passwordConfirm ? ' auth-input--valid' : '')}`}
                                type={showPasswordConfirm ? 'text' : 'password'}
                                placeholder="비밀번호 재입력"
                                value={form.passwordConfirm}
                                onChange={set('passwordConfirm')}
                                autoComplete="new-password"
                            />
                            <PasswordToggleButton visible={showPasswordConfirm} onToggle={() => setShowPasswordConfirm((v) => !v)} />
                        </div>
                        {form.passwordConfirm ? (
                            <span style={{ fontSize: 12, fontWeight: 500, color: form.password === form.passwordConfirm ? '#22c55e' : '#e53935' }}>
                                {form.password === form.passwordConfirm ? '✓ 비밀번호가 일치합니다.' : '비밀번호가 일치하지 않습니다.'}
                            </span>
                        ) : (
                            fieldError.passwordConfirm && <span className="auth-field__error">{fieldError.passwordConfirm}</span>
                        )}
                    </div>

                    {error && <p key={error} className="auth-form-error">{error}</p>}

                    <button className="auth-submit" type="submit" disabled={loading}>
                        {loading ? '변경 중...' : '비밀번호 변경'}
                    </button>
                </form>
            </div>
        </div>
    );
}
